/**
 * ProjectSessionRunner — the task-less PROJECT pod agent loop (Claudespace v3
 * project runners).
 *
 * What a project agent does today: connect, register its WorkspaceSession
 * (connectAgent), heartbeat (lease renewal via the server's v3 fallthrough),
 * and idle awaiting project-scoped messages. It deliberately does NOT run the
 * task-shaped SessionRunner lifecycle — getTaskContext, git branch sync, the
 * QueryBridge prompt loop and WIP flushing are all keyed on a Task, and the
 * server-side pm-conversation surface for project workspaces does not exist
 * yet (project chat is served by the server-side ProjectAgentService
 * responder). When that surface lands (project context RPC + project-keyed
 * message routing), this runner grows the message->query loop; until then an
 * inbound message is logged and acknowledged as activity only.
 */
import {
  AgentConnection,
  type AgentConnectionConfig,
  type IncomingMessage,
} from "../connection/agent-connection.js";
import { Lifecycle, DEFAULT_LIFECYCLE_CONFIG, type LifecycleConfig } from "./lifecycle.js";
import { sampleKeyUsage } from "../execution/usage-sampler.js";

export interface ProjectSessionRunnerConfig {
  connection: AgentConnectionConfig;
  projectId: string;
  lifecycle?: Partial<LifecycleConfig>;
}

export interface ProjectSessionRunnerCallbacks {
  onEvent?: (event: Record<string, unknown>) => void;
}

/** The AgentConnection surface the project runner uses (injectable for tests). */
export interface ProjectRunnerConnection {
  connect(): Promise<void>;
  disconnect(): void;
  call(
    method: "connectAgent",
    payload: { sessionId: string },
  ): Promise<{ sessionId: string; taskId: string | null; pendingMessages: unknown[] }>;
  sendEvent(event: { type: string; [key: string]: unknown }): void;
  sendHeartbeat(): void;
  emitStatus(status: string): Promise<void>;
  onMessage(callback: (msg: IncomingMessage) => void): void;
  onStop(callback: () => void): void;
  refreshTaskTokenFromBootstrap(): Promise<boolean>;
}

export class ProjectSessionRunner {
  readonly connection: ProjectRunnerConnection;
  readonly lifecycle: Lifecycle;

  private readonly config: ProjectSessionRunnerConfig;
  private readonly callbacks: ProjectSessionRunnerCallbacks;
  private stopped = false;
  private stopResolver: (() => void) | null = null;
  private _finalState: "finished" | "error" | null = null;

  constructor(
    config: ProjectSessionRunnerConfig,
    callbacks: ProjectSessionRunnerCallbacks = {},
    connection?: ProjectRunnerConnection,
  ) {
    this.config = config;
    this.callbacks = callbacks;
    this.connection = connection ?? new AgentConnection(config.connection);
    this.lifecycle = new Lifecycle(
      // No git flush: the project runner does not (yet) mutate the checkout,
      // and the WIP snapshot machinery is branch/task-shaped.
      { ...DEFAULT_LIFECYCLE_CONFIG, gitFlushIntervalMs: 0, ...config.lifecycle },
      {
        onHeartbeat: () => this.connection.sendHeartbeat(),
        onIdleTimeout: () => {
          process.stderr.write("[conveyor-agent] Project runner idle timeout, shutting down\n");
          this.requestStop();
        },
        onDormantTimeout: () => this.requestStop(),
        // v3 credential refresh: re-poll the pod bootstrap route (session JWT,
        // GitHub token, keys are all swapped in place). The task-scoped
        // refreshGithubToken RPC does not apply to a task-less session.
        onTokenRefresh: () => void this.connection.refreshTaskTokenFromBootstrap().catch(() => {}),
        onGitFlush: () => {},
        onUsageSample: () => void this.sampleAndReportKeyUsage(),
      },
    );
  }

  get finalState(): "finished" | "error" | null {
    return this._finalState;
  }

  get isStopped(): boolean {
    return this.stopped;
  }

  /** Connect, register the session, then idle until stopped or idle-timeout. */
  async run(): Promise<void> {
    try {
      await this.connection.connect();
      this.connection.sendEvent({
        type: "connected",
        sessionId: this.config.connection.sessionId,
        projectId: this.config.projectId,
      });

      this.connection.onStop(() => this.requestStop());
      this.connection.onMessage((msg) => this.handleMessage(msg));
      this.lifecycle.startHeartbeat();
      this.lifecycle.startTokenRefresh();
      this.lifecycle.startUsageSample();

      // Join the session room + activate the lease (server v3 fallthrough).
      await this.connection.call("connectAgent", {
        sessionId: this.config.connection.sessionId,
      });

      process.stderr.write(
        `[conveyor-agent] Project runner connected (project: ${this.config.projectId}) — idling\n`,
      );
      await this.connection.emitStatus("idle");
      this.callbacks.onEvent?.({ type: "project_runner_idle", projectId: this.config.projectId });

      this.lifecycle.startIdleTimer();
      await this.waitUntilStopped();
      this._finalState = "finished";
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      process.stderr.write(`[conveyor-agent] Project runner failed: ${message}\n`);
      this.connection.sendEvent({ type: "error", message });
      this._finalState = "error";
    } finally {
      this.shutdown();
    }
  }

  /**
   * The pm-conversation surface is a follow-up: no server path routes chat to
   * a task-less session yet, so any message that does arrive is surfaced in
   * the logs and treated as activity (idle timer restarts) — never executed.
   */
  private handleMessage(msg: IncomingMessage): void {
    const preview = msg.content.length > 120 ? `${msg.content.slice(0, 120)}…` : msg.content;
    process.stderr.write(
      `[conveyor-agent] Project runner received message (pm surface not wired yet): "${preview.replace(/\n/g, "\\n")}"\n`,
    );
    this.callbacks.onEvent?.({ type: "project_message_received", content: msg.content });
    if (!this.stopped) this.lifecycle.startIdleTimer();
  }

  /** External stop (SIGTERM/SIGINT or server session:stop). */
  stop(): void {
    this.requestStop();
  }

  private requestStop(): void {
    if (this.stopped) return;
    this.stopped = true;
    if (this.stopResolver) {
      const resolve = this.stopResolver;
      this.stopResolver = null;
      resolve();
    }
  }

  private waitUntilStopped(): Promise<void> {
    if (this.stopped) return Promise.resolve();
    return new Promise<void>((resolve) => {
      this.stopResolver = resolve;
    });
  }

  /** Report the running subscription key's rate-limit utilization (same path as
   *  the task runner). Best-effort — never throws, no-op without an OAuth token. */
  private async sampleAndReportKeyUsage(): Promise<void> {
    if (this.stopped) return;
    const samples = await sampleKeyUsage(process.env.CLAUDE_CODE_OAUTH_TOKEN);
    for (const sample of samples) {
      this.connection.sendEvent({
        type: "rate_limit_update",
        rateLimitType: sample.rateLimitType,
        utilization: sample.utilization,
        status: sample.status,
      });
    }
  }

  private shutdown(): void {
    this.stopped = true;
    this.lifecycle.destroy();
    this.connection.sendEvent({ type: "shutdown", reason: this._finalState ?? "finished" });
    this.connection.disconnect();
  }
}
